/**
 * 管理后台认证 REST API 服务
 *
 * 封装管理后台认证相关的 REST API 调用，使用 HttpOnly Cookie 存储 admin_token。
 * 所有请求使用 `credentials: 'include'` 以自动携带 Cookie。
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * 获取用户体系 Bearer token（is_admin 用户可直接认证管理后台）
 */
export function getAdminBearerToken(): string | null {
  try {
    return (
      sessionStorage.getItem('access_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('access_token')
    );
  } catch {
    return null;
  }
}

export class AdminAuthApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'AdminAuthApiError';
  }
}

export class AdminUnauthorizedError extends AdminAuthApiError {
  constructor(message: string = '未登录，请先登录管理后台') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'AdminUnauthorizedError';
  }
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // 无法解析JSON，使用默认错误消息
  }

  switch (response.status) {
    case 401:
      throw new AdminUnauthorizedError(errorMessage);
    default:
      throw new AdminAuthApiError(errorMessage, response.status);
  }
}

export interface LoginRequest {
  secret: string;
}

export interface MessageResponse {
  message: string;
}

export interface AdminStatusResponse {
  is_admin: boolean;
  message: string;
}

export class AdminAuthApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 管理员登录
   *
   * @param secret - API Secret
   * @returns 成功消息
   */
  async login(secret: string): Promise<MessageResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ secret } as LoginRequest),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 管理员登出
   *
   * @returns 成功消息
   */
  async logout(): Promise<MessageResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 验证管理员登录状态
   *
   * @returns 管理员状态，未登录时抛出 UnauthorizedError
   */
  async checkAuth(): Promise<AdminStatusResponse> {
    // 同时携带用户体系 Bearer token（is_admin 用户可直接通过认证，
    // 兼容 cookie 缺失场景）；后端优先校验 admin_token Cookie
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const userToken = getAdminBearerToken();
    if (userToken) {
      headers['Authorization'] = `Bearer ${userToken}`;
    }

    const response = await fetch(`${this.baseUrl}/api/admin/auth/me`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 检查管理员登录状态
   *
   * @returns 是否已登录
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      await this.checkAuth();
      return true;
    } catch {
      return false;
    }
  }
}

export const adminAuthApi = new AdminAuthApiService();
export default adminAuthApi;
